// Wire protocol shared between client and server.
// Every message has a `t` (type) discriminator; payloads follow.
// Single source of truth — both sides import from here.

import type { Direction, AtmospherePreset } from "../server/world/types.js";

export type Faction = "hammer" | "maleficium" | "neutral";

export interface CharacterSummary {
  id: number;
  name: string;
  faction: string;
  class: string;
  level: number;
  origin: string;
  current_zone: string;
}

export interface NpcInRoom {
  id: number;
  name: string;
  portrait_url?: string;
}
export interface ItemInRoom {
  id: number;
  template_id: number;
  name: string;
  qty: number;
  icon_url?: string;
}
export interface MobInRoom {
  id: number;
  template_id: number;
  name: string;
  hp: number;
  hp_max: number;
  portrait_url?: string;
  model_url?: string;
}
export interface OwnedItemView {
  id: number;
  template_id: number;
  name: string;
  category: string;
  sub_type?: string;
  slot: string;
  icon?: string;
  quality?: string;
  binding?: string;
  stack_limit?: number;
  qty: number;
  equipped_slot: string | null;
  icon_url?: string;
}
export interface PortalView {
  id: number;
  name: string;
  target_zone: string;
  target_zone_name?: string;
  level_req?: number;
  faction_req?: Faction;
  description?: string;
}

export interface RoomViewMsg {
  zone: string;
  room: string;
  name: string;
  description: string;
  exits: Array<{ dir: Direction; name: string; to: string; locked: boolean }>;
  npc_ids: number[];
  npcs?: NpcInRoom[];
  items?: ItemInRoom[];
  mobs?: MobInRoom[];
  visible_mobs: number[];
  /** Other player names currently in this room. */
  others: string[];
  atmosphere?: AtmospherePreset;
  /** Portals leaving this room to other zones. */
  portals?: PortalView[];
  /** Admin-attached illustration asset URL (e.g. /assets/42). */
  illustration_url?: string;
}

export type { AtmospherePreset };

export interface DialogueViewMsg {
  npc_id: number;
  npc_name: string;
  node_id: string;
  text: string;
  options: Array<{ index: number; text: string; ends: boolean }>;
  /** Optional NPC portrait URL (e.g. /assets/17). */
  portrait_url?: string;
}

export type ServerMsg =
  | { t: "hello"; client_id: string; server_time: number }
  | { t: "heartbeat"; n: number; server_time: number }
  | { t: "echo"; text: string }
  | { t: "error"; reason: string }
  | { t: "session"; token: string; account_id: number; character_id: number | null }
  | { t: "characters"; list: CharacterSummary[] }
  | { t: "character_select_required" }
  | { t: "character_created"; character: CharacterSummary }
  | { t: "spawned"; character_id: number; zone: string; room: string }
  | { t: "room"; view: RoomViewMsg }
  | { t: "denied"; reason: string }
  | { t: "system"; text: string }
  | { t: "presence"; kind: "arrive" | "leave"; who: string; from?: Direction; to?: Direction }
  | { t: "say"; who: string; text: string }
  | { t: "emote"; who: string; text: string; target?: string }
  | { t: "emotes_list"; keys: string[] }
  | { t: "alias_list"; aliases: Array<{ name: string; expansion: string; updated_at: number }> }
  | { t: "channel_list"; subscribed: Array<"trade" | "lfg" | "ooc" | "help" | "newbie">; volunteer: Array<"trade" | "lfg" | "ooc" | "help" | "newbie"> }
  | { t: "bestiary"; total_kills: number; entries: Array<{ template_id: number; name: string; species: string; encounters: number; kills: number; first_seen_at: number; first_kill_at: number }> }
  | { t: "honours"; character_name: string; totals: { kills: number; duels_won: number; duels_lost: number; quests_completed: number; gold: number; tokens: number; achievements: number; tier: string; level: number; faction: string; title: string; guild: string } }
  | { t: "leaderboard"; category: string; entries: Array<{ rank: number; character_name: string; value: number }> }
  | { t: "transmog_list"; bindings: Array<{ slot: string; appearance_template_id: number; appearance_name: string }>; unlocks: Array<{ template_id: number; name: string; slot: string; unlocked_at: number }> }
  | { t: "mount_list"; active_id: number | null; roster: Array<{ id: number; template_id: string; name: string; custom_name: string; tier: string; speed_bonus: number; acquired_at: number }> }
  | { t: "hireling_list"; active: Array<{ id: number; template_id: string; name: string; reserve_gold: number; upkeep_per_day: number; days_remaining: number; damage_bonus: number; defense_bonus: number; hp_bonus: number }> }
  | { t: "hireling_catalog"; catalog: Array<{ id: string; name: string; description: string; upkeep_per_day: number; damage_bonus: number; defense_bonus: number; hp_bonus: number }> }
  | { t: "pet_list"; summoned_id: number | null; pets: Array<{ id: number; template_id: string; name: string; custom_name: string; level: number; xp: number; hp: number; hp_max: number; damage: number; loyalty: number; hunger: number; summoned: boolean }> }
  | { t: "pet_catalog"; catalog: Array<{ id: string; name: string; description: string; species: string; base_hp: number; base_damage: number }> }
  | { t: "bounty_list"; filter: string; bounties: Array<{ id: number; poster: string; target: string; gold: number; reason: string; posted_at: number; expires_at: number; status: string }> }
  | { t: "bounty_claimed"; bounty_id: number; target: string; claimer: string; gold: number }
  | { t: "arena_rank"; rating: number; wins: number; losses: number; peak_rating: number; season_id: number; rank: number }
  | { t: "arena_leaderboard"; season_id: number; entries: Array<{ rank: number; character_name: string; rating: number; wins: number; losses: number }> }
  | { t: "pvp_status"; flag: "off" | "on" | "cooldown"; pvp_zones: string[] }
  | { t: "guild_war_list"; wars: Array<{ id: number; aggressor: string; defender: string; status: string; declared_at: number; ends_at: number; aggressor_score: number; defender_score: number; reason: string }> }
  | { t: "house_theme_list"; current: string; themes: Array<{ id: string; name: string; description_suffix: string }> }
  | { t: "guild_achievements"; entries: Array<{ id: string; name: string; description: string; goal: number; progress: number; unlocked: boolean; unlocked_at: number; reward_guild_xp: number; reward_bank_gold: number; reward_title: string }> }
  | { t: "alliance_list"; alliances: Array<{ id: number; initiator: string; partner: string; status: string; proposed_at: number }> }
  | { t: "seasons"; active: Array<{ id: string; name: string; description: string; ends_at: number }>; upcoming: Array<{ id: string; name: string; starts_at: number }> }
  | { t: "season_heralded"; kind: "start" | "end"; season_id: string; herald: string }
  | { t: "world_events"; active: Array<{ id: number; template_id: string; name: string; description: string; zone_id: string; ends_at: number; kills_credited: number; loot_bonus_gold: number }> }
  | { t: "world_event_heralded"; kind: "start" | "end"; event_id: number; template_id: string; name: string; zone_id: string; herald: string }
  | { t: "storylet_offer"; storylet_id: string; title: string; prompt: string; choices: Array<{ id: string; text: string }> }
  | { t: "storylet_resolved"; storylet_id: string; choice_id: string; result_text: string }
  | { t: "lfg_status"; queued_activity: string | null; queues: Array<{ activity_id: string; name: string; kind: string; min_level: number; party_size: number; queued_count: number }> }
  | { t: "lfg_matched"; activity_id: string; activity_name: string; character_ids: number[]; character_names: string[] }
  | { t: "dungeon_list"; entries: Array<{ id: string; name: string; description: string; min_level: number; party_min: number; party_max: number; lockout_hours: number; reward_gold: number; reward_xp: number; reward_tokens: number; locked_until: number }> }
  | { t: "dungeon_entered"; dungeon_id: string; instance_id: number; boss_name: string }
  | { t: "dungeon_cleared"; dungeon_id: string; instance_id: number; reward_gold: number; reward_xp: number; reward_tokens: number; lockout_until: number }
  | { t: "dungeon_lockouts"; lockouts: Array<{ dungeon_id: string; expires_at: number }> }
  | { t: "raid_list"; entries: Array<{ id: string; name: string; description: string; zone_id: string; min_participants: number; active_instance: boolean; locked_until: number }> }
  | { t: "raid_heralded"; raid_id: string; herald: string; zone_id: string }
  | { t: "raid_cleared"; raid_id: string; participants: Array<{ character_name: string; damage_dealt: number; reward_gold: number; reward_xp: number }> }
  | { t: "gather_result"; node_id: string; profession: string; resource_name: string; amount: number; xp_gained: number; new_level: number; leveled_up: boolean }
  | { t: "gather_skills"; skills: Array<{ profession: string; level: number; xp: number; next_level_xp: number }> }
  | { t: "craft_result"; recipe_id: string; recipe_name: string; amount: number; xp_gained: number; new_level: number; leveled_up: boolean; quality?: "poor" | "normal" | "fine" | "masterwork"; bonus_qty?: number; rare_proc?: boolean; station_tier?: number }
  | { t: "craft_recipes"; profession: string; recipes: Array<{ id: string; name: string; min_skill: number; available: boolean; gold_cost: number; ingredients: Array<{ template_id: number; name: string; qty: number }>; output_name: string; output_qty: number }> }
  | { t: "craft_skills"; skills: Array<{ profession: string; level: number; xp: number; next_level_xp: number }> }
  | { t: "market_book"; template_id: number; buys: Array<{ order_id: number; price: number; qty: number; seller_char_name: string }>; sells: Array<{ order_id: number; price: number; qty: number; seller_char_name: string }> }
  | { t: "market_placed"; order_id: number; side: "buy" | "sell"; template_id: number; price: number; qty: number }
  | { t: "market_filled"; your_role: "buyer" | "seller"; template_id: number; template_name: string; qty: number; price_per_unit: number; total_gold: number; counterparty_name: string }
  | { t: "market_mine"; orders: Array<{ order_id: number; side: "buy" | "sell"; template_id: number; template_name: string; price: number; qty: number; original_qty: number; placed_at: number }> }
  | { t: "market_history"; template_id: number; template_name: string; trades: Array<{ price: number; qty: number; executed_at: number; buyer: string; seller: string }> }
  | { t: "market_trend"; template_id: number; template_name: string; days: Array<{ day: string; avg_price: number; volume: number }> }
  | { t: "craft_order_list"; filter: string; orders: Array<{ id: number; client: string; output_name: string; output_qty: number; bounty_gold: number; status: string; committed_by_name: string; note: string; ingredients: Array<{ name: string; qty: number }> }> }
  | { t: "craft_order_update"; order_id: number; status: string; message: string }
  | { t: "shop_browse"; shops: Array<{ shop_id: number; name: string; owner: string; zone_id: string; room_id: string; stock_preview: Array<{ stock_id: number; name: string; qty: number; price: number }> }> }
  | { t: "shop_mine"; shop: null | { id: number; name: string; zone_id: string; room_id: string; till_gold: number; status: string; stock: Array<{ stock_id: number; template_id: number; name: string; qty: number; price: number }> } }
  | { t: "shop_transaction"; role: "buyer" | "owner"; stock_id: number; template_name: string; qty: number; total_gold: number; counterparty: string }
  | { t: "chat"; channel: "global" | "faction" | "whisper" | "party" | "trade" | "lfg" | "ooc" | "help" | "newbie"; who: string; text: string; to?: string }
  | { t: "dialogue"; view: DialogueViewMsg }
  | { t: "dialogue_end"; npc_id: number }
  | { t: "inventory"; items: OwnedItemView[] }
  | { t: "picked_up"; item: OwnedItemView }
  | { t: "dropped"; template_id: number; name: string; qty: number }
  | { t: "wallet"; gold: number }
  | { t: "shop_view"; vendor_id: number; vendor_name: string; sells: Array<{ template_id: number; name: string; price: number; in_stock: boolean }>; buyback_factor: number }
  | { t: "transaction"; kind: "buy" | "sell"; name: string; qty: number; gold_delta: number; gold_after: number }
  | { t: "combat"; round: { attacker_dmg: number; mob_dmg: number; mob_hp: number; mob_hp_max: number; player_hp: number; player_hp_max: number; mob_name: string; ability_name?: string; crit?: boolean } }
  | { t: "combat_started"; mob_id: number; mob_template_id: number; mob_name: string; mob_hp: number; mob_hp_max: number; abilities: Array<{ id: string; name: string; description: string; cost: number; cooldown_rounds: number }> }
  | { t: "combat_ended"; reason: "mob_killed" | "you_died" | "fled" | "mob_fled" | "disengaged" }
  | { t: "flee_failed"; counter_dmg: number; player_hp: number; player_hp_max: number }
  | { t: "killed"; mob_name: string; xp_gained: number; xp_total: number; loot: string[] }
  | { t: "died"; respawn_zone: string; respawn_room: string; gold_lost: number; cause: string }
  | { t: "stats"; level: number; xp: number; hp: number; hp_max: number; mp: number; mp_max: number }
  | { t: "quests"; entries: Array<{ id: number; name: string; description: string; status: string; progress: Array<{ kind: string; target: string; have: number; need: number }>; ready_to_turn_in: boolean }> }
  | { t: "quest_offered"; npc_name: string; quest_id: number; name: string; description: string }
  | { t: "quest_assigned"; quest_id: number; name: string }
  | { t: "quest_progress"; quest_id: number; name: string; progress: Array<{ have: number; need: number; target: string }> }
  | { t: "quest_completed"; quest_id: number; name: string; xp: number; gold: number; items: string[] }
  | { t: "skill_tree"; class_name: string; skills: Array<{ id: string; name: string; description: string; rank: number; max_rank: number }>; points_available: number }
  | { t: "skill_trained"; skill_id: string; new_rank: number; points_remaining: number }
  | { t: "who"; players: Array<{ name: string; faction: string; class: string; level: number; zone: string; room: string }> }
  | { t: "reputation"; hammer: number; maleficium: number; neutral: number; title: string }
  | { t: "rep_gain"; faction: string; delta: number; new_value: number; new_title?: string }
  | { t: "party_invitation"; party_id: number; inviter: string }
  | { t: "party"; party_id: number; leader: string; members: Array<{ name: string; level: number; class: string; zone: string; room: string }> }
  | { t: "party_left" }
  | { t: "trade_request"; trade_id: number; from: string }
  | { t: "trade_window"; trade_id: number; you: { name: string; items: Array<{ instance_id: number; name: string; qty: number }>; gold: number; confirmed: boolean }; them: { name: string; items: Array<{ instance_id: number; name: string; qty: number }>; gold: number; confirmed: boolean } }
  | { t: "trade_complete"; trade_id: number; you_received: { items: string[]; gold: number }; you_gave: { items: string[]; gold: number } }
  | { t: "trade_cancelled"; trade_id: number; reason: string }
  | { t: "zone_map"; zone: string; zone_name: string; rooms: Array<{ id: string; name: string; x: number; y: number }>; edges: Array<{ from: string; to: string }> }
  | { t: "entitlements"; tier: "free" | "adventurer" | "patron"; flags: {
        char_slots: number; inv_slots: number; bank_tabs: number;
        private_cottage: boolean; cottage_garden: boolean;
        housing_placeables: number; housing_guest_access: boolean;
        can_found_guild: boolean; shop_listings: number; trade_concurrency: number;
        cosmetic_titles: boolean; custom_emotes: boolean;
        subscriber_zones: boolean; early_access_days: number;
        dungeon_resets_per_week: number; recipe_library: boolean;
        priority_queue: boolean; minimap_pins: boolean; log_export: boolean;
        volvelle_advanced: boolean; anchor_inspector: boolean;
      } }
  | { t: "custom_emote"; who: string; action: string }
  | { t: "title_set"; character_id: number; title: string | null }
  | { t: "guild_info"; guild: null | { id: number; name: string; rank: string; members: Array<{ character_id: number; name: string; rank: string }>; progress?: { tier: number; tier_name: string; xp: number; unlocked_rooms: string[]; required_members: number; current_members: number; bank_tabs: number; next_tier: null | { tier: number; name: string; required_xp: number; required_members: number } } } }
  | { t: "guild_invitation"; guild_id: number; guild_name: string; from: string }
  | { t: "token_balance"; balance: number; lifetime_earned: number; days_until_next_grant: number; last_grant_delta?: number }
  | { t: "tenure_status"; paid_days: number; claimed: Array<{ id: number; days: number; name: string; reward: string }>; unclaimed: Array<{ id: number; days: number; name: string; reward: string }>; upcoming: Array<{ id: number; days: number; name: string; reward: string; days_remaining: number }> }
  | { t: "tenure_claimed"; milestone: { id: number; days: number; name: string; reward: string } }
  | { t: "rename_result"; character_id: number; old_name: string; new_name: string; cooldown_days_remaining: number }
  | { t: "mail_inbox"; messages: Array<{ id: number; sender: string; subject: string; body: string; attached_gold: number; cod_gold: number; sent_at: number; read_at: number; expires_at: number; direction: "inbox" | "sent" }>; inbox_count: number; inbox_capacity: number }
  | { t: "mail_sent_ok"; mail_id: number }
  | { t: "mail_received"; from: string; subject: string; mail_id: number }
  | { t: "auction_listings"; listings: Array<{ id: number; seller: string; template_id: number; item_name: string; qty: number; asking_gold: number; buyout_gold: number; has_reserve: boolean; reserve_met: boolean; current_bid: number; listed_at: number; expires_at: number; is_mine: boolean; status: string }>; my_active: number; my_capacity: number }
  | { t: "auction_listed"; listing_id: number; item_name: string; qty: number; asking_gold: number; fee_paid: number }
  | { t: "auction_sold"; listing_id: number; item_name: string; qty: number; gold_gained: number; buyer: string }
  | { t: "auction_bought"; listing_id: number; item_name: string; qty: number; gold_spent: number; seller: string }
  | { t: "auction_cancelled"; listing_id: number; item_name: string; reason: "cancelled" | "expired" }
  | { t: "auction_bid_placed"; listing_id: number; bid_gold: number; min_next_bid: number }
  | { t: "auction_outbid"; listing_id: number; item_name: string; new_bid: number; refund: number }
  | { t: "auction_won"; listing_id: number; item_name: string; qty: number; winning_bid: number }
  | { t: "cottage_furniture"; items: Array<{ id: number; template_id: number; name: string; note: string; placed_at: number }>; placed: number; capacity: number }
  | { t: "bank_view"; tabs: number; labels: Record<number, string>; items: Array<{ id: number; tab: number; template_id: number; name: string; qty: number; deposited_at: number }> }
  | { t: "guild_bank_view"; items: Array<{ id: number; tab: number; template_id: number; name: string; qty: number; deposited_by: string; deposited_at: number }>; log: Array<{ actor: string; action: string; item_name: string; qty: number; ts: number }> }
  | { t: "accusation_update"; accusation: { id: number; accuser: string; accused: string; jurisdiction: string; type: string; status: string; required_evidence: number; current_evidence: number; investigation_deadline: number; filed_at: number; verdict: string; sentence: string; sentence_amount: number; sentence_expires: number } }
  | { t: "accusation_list"; cases: Array<{ id: number; accuser: string; accused: string; jurisdiction: string; type: string; status: string; current_evidence: number; required_evidence: number; verdict: string; filed_at: number }> }
  | { t: "accusation_detail"; accusation: { id: number; accuser: string; accused: string; jurisdiction: string; type: string; text: string; status: string; required_evidence: number; current_evidence: number; investigation_deadline: number; verdict: string; sentence: string; sentence_amount: number; sentence_expires: number; appeal_deadline: number; filed_at: number; resolved_at: number }; evidence: Array<{ id: number; type: string; name: string; description: string; credibility: number; weight: number; validation: string; presented_by: string }>; testimony: Array<{ id: number; witness: string; type: string; text: string; strength: number }> }
  | { t: "accusation_verdict"; accusation_id: number; accused: string; verdict: string; sentence: string; sentence_amount: number; appeal_deadline: number }
  | { t: "inquisitor_rank"; level: number; rank_name: string; investigations_completed: number; trials_presided: number; authority: number; next_rank: null | { level: number; name: string; required_rep: number; required_investigations: number; required_trials: number } }
  | { t: "voynich_encoded"; input: string; encoded: string; quality: number; section: string; font_variant: string }
  | { t: "voynich_decoded"; input: string; confidence: number; unknown_slots: number; total_slots: number; words: Array<{ raw: string; prefix: string; gallows: string; core: string; suffix: string; role: string; known: boolean }> }
  | { t: "voynich_cast"; caster: string; encoded: string; section: string; quality: number; font_variant: string }
  | { t: "scribe_mastery"; mastery: number; level: number; level_name: string; glyphs_learned: number; next_level_threshold: number | null }
  | { t: "glyph_learned"; glyph: string; difficulty: number; new_mastery: number }
  | { t: "glyphs_known"; glyphs: string[]; count: number }
  | { t: "coven_info"; coven: null | { id: number; name: string; founder: string; moon_affinity: string; rank: string; members: Array<{ character_id: number; name: string; rank: string }> } }
  | { t: "coven_invitation"; invite_id: number; coven_id: number; coven_name: string; from: string; expires_at: number }
  | { t: "ritual_list"; templates: Array<{ id: string; name: string; type: string; required_mastery: number; required_moon: string; required_participants: number; duration_sec: number; description: string }>; moon_phase: string }
  | { t: "ritual_started"; ritual_id: number; template_id: string; name: string; initiator: string; expires_at: number }
  | { t: "ritual_completed"; ritual_id: number; template_id: string; success: boolean; reason: string; effect_summary: string }
  | { t: "ritual_joined"; ritual_id: number; participant: string }
  | { t: "standing_view"; standings: Array<{ faction: string; value: number; tier: string; label: string; consequences: Array<{ kind: string; note: string; tier: string }> }> }
  | { t: "standing_change"; faction: string; delta: number; value: number; tier: string; new_consequences: Array<{ kind: string; note: string; tier: string }> }
  | { t: "betrayal_result"; from_faction: string; to_faction: string; before_from: number; after_from: number; before_to: number; after_to: number }
  | { t: "agent_registered"; real_faction: string; apparent_faction: string; secret_key: string }
  | { t: "agent_discovered"; who: string; real_faction: string; apparent_faction: string }
  | { t: "disguise_applied"; template_id: string; name: string; target_faction: string; expires_at: number }
  | { t: "disguise_detected"; who: string; disguise_name: string; apparent_faction: string }
  | { t: "moon_tick"; phase: "new" | "waxing" | "full" | "waning"; glyph: string; seconds_until_next: number; cycle_seconds: number }
  | { t: "territory_status"; territories: Array<{ zone: string; coven_id: number; coven_name: string; contested: boolean; contest_expires?: number; contested_by?: string }> }
  | { t: "territory_claimed"; zone: string; coven_name: string }
  | { t: "territory_contested"; zone: string; challenger_coven: string; expires_at: number }
  | { t: "territory_resolved"; zone: string; winner_coven_name: string | null; winner_side: "holder" | "challenger" | "draw" }
  | { t: "prayer_list"; templates: Array<{ id: string; name: string; type: string; required_rep: number; required_participants: number; duration_sec: number; description: string }> }
  | { t: "prayer_started"; prayer_id: number; template_id: string; name: string; leader: string; expires_at: number }
  | { t: "prayer_completed"; prayer_id: number; template_id: string; success: boolean; reason: string; effect_summary: string }
  | { t: "exorcism_started"; id: number; exorcist: string; target: string; target_hp: number; exorcist_power: number; max_rounds: number }
  | { t: "exorcism_round"; id: number; round: number; action: string; damage: number; narration: string; target_hp: number; exorcist_power: number }
  | { t: "exorcism_ended"; id: number; status: "success" | "failure" | "fled"; summary: string }
  | { t: "primi_recipes"; recipes: Array<{ id: string; name: string; required_mastery: number; difficulty: number; keywords: string[]; ingredients: string[]; description: string }> }
  | { t: "primi_crafted"; recipe_id: string; item_name: string; instance_id: number; quality: number }
  | { t: "friend_list"; friends: Array<{ char_id: number; name: string; status: "pending" | "accepted"; direction: "outgoing" | "incoming" | "mutual"; online: boolean; zone: string }> }
  | { t: "friend_request_received"; from_char_id: number; from_name: string }
  | { t: "block_list"; blocks: Array<{ char_id: number; name: string; blocked_at: number; reason: string }> }
  | { t: "daily_status"; current_streak_day: number; claimable_day: number | null; can_claim: boolean; seconds_until_next_utc: number; cycle: Array<{ day: number; kind: "gold" | "tokens" | "item" | "xp"; amount: number; label: string }> }
  | { t: "daily_claimed"; day: number; kind: "gold" | "tokens" | "item" | "xp"; amount: number; label: string }
  | { t: "daily_quests"; date: string; quests: Array<{ id: string; title: string; description: string; objective_text: string; progress: number; goal: number; completed: boolean; ready: boolean; reward_gold: number; reward_xp: number }> }
  | { t: "daily_quest_progress"; id: string; title: string; progress: number; goal: number; ready: boolean }
  | { t: "daily_quest_completed"; id: string; title: string; reward_gold: number; reward_xp: number }
  | { t: "duel_invitation"; duel_id: number; from: string; to: string }
  | { t: "duel_started"; duel_id: number; challenger: string; defender: string; round: number; your_hp: number; opp_hp: number; opp_name: string }
  | { t: "duel_round"; duel_id: number; round: number; your_hp: number; opp_hp: number; your_action: "strike" | "parry" | "rest"; opp_action: "strike" | "parry" | "rest"; log: string }
  | { t: "duel_ended"; duel_id: number; winner_name: string; your_result: "won" | "lost" | "draw"; wins: number; losses: number }
  | { t: "duel_record"; wins: number; losses: number }
  | { t: "achievements"; entries: Array<{ id: string; name: string; description: string; category: "combat" | "social" | "exploration" | "mastery" | "pvp" | "milestone"; goal: number; progress: number; unlocked: boolean; unlocked_at: number; reward_gold: number; reward_xp: number; reward_title?: string }> }
  | { t: "achievement_unlocked"; id: string; name: string; description: string; reward_gold: number; reward_xp: number; reward_title?: string }
  | { t: "tutorial_status"; step: number; total_steps: number; completed: boolean; reward_claimed: boolean; hint: string | null }
  | { t: "tutorial_advanced"; step: number; total_steps: number; completed: boolean; hint: string | null }
  | { t: "tutorial_reward"; gold: number; tokens: number; xp: number }
  | { t: "house_status"; tier: "cottage" | "manor" | "keep" | "castle"; tier_name: string; description: string; placeable_bonus: number; guest_cap: number; next: null | { tier: "cottage" | "manor" | "keep" | "castle"; name: string; cost_gold: number; cost_tokens: number; min_tenure_days: number; has_gold: boolean; has_tokens: boolean; has_tenure: boolean } }
  | { t: "cottage_guests_list"; guests: Array<{ character_id: number; name: string; added_at: number; expires_at: number }> }
  | { t: "house_upgraded"; tier: "cottage" | "manor" | "keep" | "castle"; tier_name: string; gold_spent: number; tokens_spent: number }
  | { t: "library_locked"; reason: string }
  | { t: "library_catalog"; books: Array<{ id: number; slug: string; title: string; author: string; year: string; page_count: number; last_page: number }> }
  | { t: "library_page"; book: { id: number; slug: string; title: string; author: string; year: string; page_count: number; last_page: number }; page_no: number; title: string; body: string; has_prev: boolean; has_next: boolean }
  | { t: "library_search"; query: string; hits: Array<{ book_id: number; book_title: string; page_no: number; page_title: string; snippet: string }> }
  // Living world + new systems
  | { t: "divination"; figure: string; house: number; reading_text: string; npc_action: string; condition: string }
  | { t: "divination_cooldown"; remaining_ms: number }
  | { t: "alchemy_workbench"; recipe_id: string; step: number; slots: number; quality: number }
  | { t: "alchemy_result"; recipe_name: string; quality: number; xp: number }
  | { t: "ascension_result"; new_tier: string; ascension_count: number; xp_bonus_pct: number }
  | { t: "interact_result"; object_name: string; kind: string; message: string; ok: boolean }
  | { t: "gamble_result"; game: string; won: boolean; wager: number; payout: number }
  | { t: "exchange_result"; from: string; to: string; amount_in: number; amount_out: number }
  | { t: "recipe_catalog"; entries: Array<{ id: number; name: string; category: string; danger_level: number }> }
  | { t: "recipe_entry"; name: string; body: string; disclaimer: string }
  | { t: "farm_planted"; crop: string; ready_at: number }
  | { t: "farm_harvested"; crop: string; qty: number }
  | { t: "vessel_status"; vessels: Array<{ id: number; name: string; status: string; zone: string }> }
  | { t: "book_written"; book_id: number; title: string }
  | { t: "language_list"; languages: Array<{ id: string; name: string; proficiency: number }> }
  | { t: "npc_chatter"; lines: Array<{ speaker: string; text: string }> }
  | { t: "crier_shout"; text: string }
  | { t: "bard_ballad"; title: string; verses: string[][] }
  | { t: "ghost_visible"; ghosts: Array<{ name: string; cause: string }> }
  | { t: "possession_symptom"; text: string; stage: number }
  | { t: "curfew_warning"; zone: string };

export type ClientMsg =
  | { t: "ping"; client_time: number }
  | { t: "register"; username: string; password: string; password_confirm: string; email?: string }
  | { t: "login"; username: string; password: string }
  | { t: "resume"; token: string }
  | { t: "list_characters" }
  | { t: "create_character"; name: string; faction: Faction; class: string; origin: string; gender: "male" | "female" }
  | { t: "select_character"; id: number }
  | { t: "look" }
  | { t: "move"; dir: Direction }
  | { t: "say"; text: string }
  | { t: "global"; text: string }
  | { t: "faction_chat"; text: string }
  | { t: "whisper"; to: string; text: string }
  | { t: "who" }
  | { t: "logout" }
  | { t: "talk"; npc_id_or_name: number | string }
  | { t: "reply"; option_index: number }
  | { t: "end_talk" }
  | { t: "inventory" }
  | { t: "get"; what: string | number; qty?: number }
  | { t: "drop"; instance_id: number; qty?: number }
  | { t: "equip"; instance_id: number }
  | { t: "unequip"; slot: string }
  | { t: "wallet" }
  | { t: "shop"; npc_id_or_name: number | string }
  | { t: "buy"; npc_id_or_name: number | string; template_id: number; qty?: number }
  | { t: "sell"; npc_id_or_name: number | string; instance_id: number; qty?: number }
  | { t: "attack"; mob_id_or_name: number | string }
  | { t: "stats" }
  | { t: "quests" }
  | { t: "quests_offered"; npc_id_or_name: number | string }
  | { t: "accept_quest"; quest_id: number }
  | { t: "abandon_quest"; quest_id: number }
  | { t: "turn_in"; quest_id: number }
  | { t: "skill_tree" }
  | { t: "train_skill"; skill_id: string }
  | { t: "reputation" }
  | { t: "party_invite"; name: string }
  | { t: "party_accept"; party_id: number }
  | { t: "party_leave" }
  | { t: "party" }
  | { t: "party_chat"; text: string }
  | { t: "portal"; portal_id: number }
  | { t: "switch_character" }
  | { t: "trade_request_send"; to: string }
  | { t: "trade_accept"; trade_id: number }
  | { t: "trade_offer_item"; trade_id: number; instance_id: number; qty?: number }
  | { t: "trade_offer_gold"; trade_id: number; gold: number }
  | { t: "trade_confirm" }
  | { t: "trade_cancel" }
  | { t: "ability"; ability_id: string; mob_id_or_name: number | string }
  | { t: "flee" }
  | { t: "custom_emote"; action: string }
  | { t: "set_title"; title: string }
  | { t: "guild_found"; name: string }
  | { t: "guild_invite"; target: string }
  | { t: "guild_accept"; guild_id: number }
  | { t: "guild_decline"; guild_id: number }
  | { t: "guild_leave" }
  | { t: "guild_info" }
  | { t: "home" }
  | { t: "leave_home" }
  | { t: "tokens" }
  | { t: "tenure" }
  | { t: "tenure_claim"; milestone_id: number }
  | { t: "rename"; new_name: string }
  | { t: "mail_send"; to: string; subject: string; body: string; gold?: number; cod_gold?: number; item_instance_id?: number; item_qty?: number }
  | { t: "mail_inbox" }
  | { t: "mail_sent_box" }
  | { t: "mail_read"; mail_id: number; accept_cod?: boolean }
  | { t: "mail_delete"; mail_id: number }
  | { t: "auction_list"; instance_id: number; qty: number; asking_gold: number; buyout_gold?: number; reserve_gold?: number }
  | { t: "auction_bid"; listing_id: number; bid_gold: number }
  | { t: "cottage_place"; instance_id: number; note?: string }
  | { t: "cottage_remove"; furniture_id: number }
  | { t: "cottage_list" }
  | { t: "bank_list"; tab?: number; sort_by?: "name" | "qty" | "time" }
  | { t: "bank_set_label"; tab: number; label: string }
  | { t: "bank_deposit"; instance_id: number; tab?: number; qty?: number }
  | { t: "bank_withdraw"; bank_id: number; qty?: number }
  | { t: "guild_bank_list"; tab?: number }
  | { t: "guild_bank_deposit"; instance_id: number; tab?: number; qty?: number }
  | { t: "guild_bank_withdraw"; bank_id: number }
  | { t: "accuse"; target: string; accusation_type: "witchcraft" | "heresy" | "corruption" | "blasphemy" | "possession"; jurisdiction: "hammer" | "maleficium"; text?: string }
  | { t: "evidence"; accusation_id: number; evidence_type: "physical" | "testimonial" | "supernatural" | "circumstantial" | "documentary"; name: string; description?: string; discovery_location?: string }
  | { t: "testify"; accusation_id: number; testimony_type: "character" | "expert" | "eyewitness"; text: string }
  | { t: "cases"; filter?: "all" | "mine" | "against_me" | "active" }
  | { t: "case_detail"; accusation_id: number }
  | { t: "appeal"; accusation_id: number }
  | { t: "inquisitor_status" }
  | { t: "cast"; text: string; section?: "H" | "A" | "P" | "S" | "Z"; font_variant?: "regular" | "3d" | "italic" | "leftalic" | "3d-italic" | "3d-leftalic" }
  | { t: "decode"; text: string }
  | { t: "learn_glyph"; glyph: string }
  | { t: "mastery" }
  | { t: "glyphs" }
  | { t: "coven_found"; name: string }
  | { t: "coven_invite"; target: string }
  | { t: "coven_accept"; invite_id: number }
  | { t: "coven_decline"; invite_id: number }
  | { t: "coven_leave" }
  | { t: "coven_kick"; target: string }
  | { t: "coven_promote"; target: string; rank: "master" | "initiate" }
  | { t: "coven_info" }
  | { t: "ritual_list" }
  | { t: "ritual_start"; template_id: string; target_accusation_id?: number }
  | { t: "ritual_join"; ritual_id: number }
  | { t: "ritual_cancel"; ritual_id: number }
  | { t: "standing"; faction?: "hammer" | "maleficium" }
  | { t: "betray"; from_faction: "hammer" | "maleficium"; to_faction: "hammer" | "maleficium" }
  | { t: "register_agent"; real_faction: "hammer" | "maleficium"; apparent_faction: "hammer" | "maleficium" | "neutral"; secret_key: string }
  | { t: "unregister_agent" }
  | { t: "disguise_apply"; template_id: string }
  | { t: "disguise_remove" }
  | { t: "disguise_list" }
  | { t: "evidence_voynich"; accusation_id: number; voynich_text: string; note?: string }
  | { t: "moon" }
  | { t: "territory_list" }
  | { t: "territory_claim" }
  | { t: "territory_contest" }
  | { t: "prayer_list" }
  | { t: "prayer_start"; template_id: string; target_accusation_id?: number }
  | { t: "prayer_join"; prayer_id: number }
  | { t: "prayer_cancel"; prayer_id: number }
  | { t: "exorcism_begin"; target: string }
  | { t: "exorcism_action"; id: number; action: "pray" | "exhort" | "banish" }
  | { t: "exorcism_flee"; id: number }
  | { t: "primi_list" }
  | { t: "primi_craft"; recipe_id: string; voynich_text: string }
  | { t: "report"; target: string; category: "harassment" | "cheating" | "bug" | "spam" | "other"; text: string }
  | { t: "auction_browse"; query?: string }
  | { t: "auction_mine" }
  | { t: "auction_buy"; listing_id: number }
  | { t: "auction_cancel"; listing_id: number }
  | { t: "friend_request"; target: string }
  | { t: "friend_accept"; friend_char_id: number }
  | { t: "friend_decline"; friend_char_id: number }
  | { t: "friend_remove"; friend_char_id: number }
  | { t: "friend_list" }
  | { t: "block_add"; target: string; reason?: string }
  | { t: "block_remove"; char_id: number }
  | { t: "block_list" }
  | { t: "daily_status" }
  | { t: "daily_claim" }
  | { t: "daily_quests" }
  | { t: "daily_quest_turn_in"; quest_id: string }
  | { t: "duel_invite"; target: string }
  | { t: "duel_accept"; duel_id: number }
  | { t: "duel_decline"; duel_id: number }
  | { t: "duel_action"; duel_id: number; action: "strike" | "parry" | "rest" }
  | { t: "duel_forfeit"; duel_id: number }
  | { t: "duel_record" }
  | { t: "achievements" }
  | { t: "tutorial_status" }
  | { t: "tutorial_skip" }
  | { t: "tutorial_claim" }
  | { t: "cottage_visit"; host: string }
  | { t: "cottage_invite"; guest: string }
  | { t: "cottage_uninvite"; guest: string }
  | { t: "cottage_guests" }
  | { t: "house_status" }
  | { t: "house_upgrade" }
  | { t: "guild_hall_enter" }
  | { t: "guild_hall_leave" }
  | { t: "emote"; key: string; target?: string }
  | { t: "emotes_list" }
  | { t: "alias_list" }
  | { t: "alias_set"; name: string; expansion: string }
  | { t: "alias_remove"; name: string }
  | { t: "channel_send"; channel: "trade" | "lfg" | "ooc" | "help" | "newbie"; text: string }
  | { t: "channel_join"; channel: "trade" | "lfg" | "ooc" | "help" | "newbie" }
  | { t: "channel_leave"; channel: "trade" | "lfg" | "ooc" | "help" | "newbie" }
  | { t: "channel_list" }
  | { t: "bestiary" }
  | { t: "honours"; character_name?: string }
  | { t: "leaderboard"; category: "kills" | "duels" | "gold" | "achievements" | "guild_xp" }
  | { t: "skill_respec" }
  | { t: "transmog_bind"; slot: string; appearance_template_id: number }
  | { t: "transmog_clear"; slot: string }
  | { t: "transmog_list" }
  | { t: "mount_list" }
  | { t: "mount_summon"; roster_id: number }
  | { t: "mount_dismiss" }
  | { t: "mount_rename"; roster_id: number; name: string }
  | { t: "hireling_list" }
  | { t: "hireling_hire"; template_id: string; reserve_gold: number }
  | { t: "hireling_dismiss"; id: number }
  | { t: "hireling_topup"; id: number; gold: number }
  | { t: "hireling_catalog" }
  | { t: "pet_list" }
  | { t: "pet_acquire"; template_id: string; name?: string }
  | { t: "pet_summon"; pet_id: number }
  | { t: "pet_dismiss"; pet_id: number }
  | { t: "pet_feed"; pet_id: number; instance_id: number }
  | { t: "pet_release"; pet_id: number }
  | { t: "pet_catalog" }
  | { t: "bounty_post"; target: string; gold: number; reason: string }
  | { t: "bounty_cancel"; bounty_id: number }
  | { t: "bounty_list"; filter?: "active" | "on_me" | "mine" }
  | { t: "arena_rank" }
  | { t: "arena_leaderboard" }
  | { t: "pvp_flag"; on: boolean }
  | { t: "pvp_status" }
  | { t: "guild_war_declare"; target_guild_name: string; reason: string }
  | { t: "guild_war_accept"; war_id: number }
  | { t: "guild_war_surrender"; war_id: number }
  | { t: "guild_war_list" }
  | { t: "house_theme_list" }
  | { t: "house_theme_set"; theme_id: string }
  | { t: "guild_achievements" }
  | { t: "guild_rank_label"; rank: "founder" | "officer" | "member"; label: string }
  | { t: "alliance_propose"; partner_guild_name: string }
  | { t: "alliance_accept"; alliance_id: number }
  | { t: "alliance_decline"; alliance_id: number }
  | { t: "alliance_dissolve"; alliance_id: number }
  | { t: "alliance_list" }
  | { t: "seasons" }
  | { t: "world_events" }
  | { t: "storylet_roll" }
  | { t: "storylet_choose"; storylet_id: string; choice_id: string }
  | { t: "lfg_join"; activity_id: string }
  | { t: "lfg_leave" }
  | { t: "lfg_list" }
  | { t: "dungeon_list" }
  | { t: "dungeon_enter"; dungeon_id: string }
  | { t: "dungeon_lockouts" }
  | { t: "raid_list" }
  | { t: "gather"; profession?: "fishing" | "mining" | "herbalism" | "logging" }
  | { t: "gather_skills" }
  | { t: "craft"; recipe_id: string }
  | { t: "craft_recipes"; profession?: "smithing" | "alchemy" | "cooking" | "tailoring" }
  | { t: "craft_skills" }
  | { t: "market_place"; side: "buy" | "sell"; template_id: number; price: number; qty: number; instance_id?: number }
  | { t: "market_cancel"; order_id: number }
  | { t: "market_book"; template_id: number }
  | { t: "market_mine" }
  | { t: "market_history"; template_id: number }
  | { t: "market_trend"; template_id: number; days?: number }
  | { t: "craft_order_post"; output_template_id: number; output_qty: number; bounty_gold: number; ingredients: Array<{ template_id: number; qty: number }>; note: string }
  | { t: "craft_order_commit"; order_id: number }
  | { t: "craft_order_deliver"; order_id: number }
  | { t: "craft_order_cancel"; order_id: number }
  | { t: "craft_order_list"; filter?: "open" | "mine_client" | "mine_crafter" }
  | { t: "shop_open"; name: string; initial_till: number }
  | { t: "shop_close" }
  | { t: "shop_stock_add"; instance_id: number; qty: number; price: number }
  | { t: "shop_stock_remove"; stock_id: number }
  | { t: "shop_withdraw"; amount: number }
  | { t: "shop_buy"; stock_id: number; qty: number }
  | { t: "shop_browse" }
  | { t: "shop_mine" }
  | { t: "library_catalog" }
  | { t: "library_open"; book_id: number }
  | { t: "library_page"; book_id: number; page_no: number }
  | { t: "library_search"; book_id?: number; query: string }
  // Living world + new systems
  | { t: "divine" }
  | { t: "alchemy_start"; recipe_id: string }
  | { t: "alchemy_add"; ingredient_id: string; prep: string }
  | { t: "alchemy_process"; method: string }
  | { t: "alchemy_finalize" }
  | { t: "ascend" }
  | { t: "interact"; object_id: string }
  | { t: "gamble"; game: string; wager: number }
  | { t: "exchange"; from: string; to: string; amount: number }
  | { t: "recipe_catalog" }
  | { t: "recipe_read"; slug: string }
  | { t: "recipe_search"; query: string }
  | { t: "plant"; crop_id: string }
  | { t: "harvest"; plot_id: number }
  | { t: "vessel_list" }
  | { t: "vessel_embark"; vessel_id: number; waterway_id: string }
  | { t: "write_book"; title: string; pages: string[] }
  | { t: "languages" }
  | { t: "speak_language"; language_id: string; text: string };

export const PROTOCOL_VERSION = 2;
export const HEARTBEAT_INTERVAL_MS = 5000;
export const SERVER_PORT = 8400;

// Re-export Direction so the client can import it from one place.
export type { Direction };
